import { useMemo, type ReactNode } from "react";
import { Checkbox as ZhCheckbox } from "zhihao-ui-react";

import { authList } from "@/views/pages/manageCenter/systemManage/role/components/authList";

export interface RolePermissionSelectorProps {
    value?: string[];
    onChange?: (value: string[]) => void;
    disabled?: boolean;
}

interface RolePermissionOption {
    auth: string;
    label: string;
    path: string[];
    groupKey: string;
    permissionIndex: number;
}

interface RolePermissionNode {
    label: string;
    path: string[];
    children: RolePermissionNode[];
    options: RolePermissionOption[];
}

/** 展平 Vue 权限树的叶子动作，同时保留树的固定顺序用于提交数组。 */
function collectOptions(nodes: typeof authList, path: string[] = []): RolePermissionOption[] {
    return nodes.flatMap(node => {
        const nextPath = [...path, node.label];
        const groupKey = nextPath.join(" / ");
        const ownOptions = (node.authOptions || []).map((option, permissionIndex) => ({
            auth: option.auth,
            label: option.label,
            path: nextPath,
            groupKey,
            permissionIndex
        }));
        return [...ownOptions, ...collectOptions(node.children || [], nextPath)];
    });
}

/** 把 Vue 权限树转换为可递归渲染的节点，保留中间节点的批量选择能力。 */
function collectTree(nodes: typeof authList, path: string[] = []): RolePermissionNode[] {
    return nodes.map(node => {
        const nextPath = [...path, node.label];
        return {
            label: node.label,
            path: nextPath,
            children: collectTree(node.children || [], nextPath),
            options: (node.authOptions || []).map((option, permissionIndex) => ({
                auth: option.auth,
                label: option.label,
                path: nextPath,
                groupKey: nextPath.join(" / "),
                permissionIndex
            }))
        };
    });
}

/** React 角色表单复用 Vue `authList`，提交仍为权限编码数组。 */
export function RolePermissionSelector({ value = [], onChange, disabled = false }: RolePermissionSelectorProps) {
    const groups = useMemo<RolePermissionNode[]>(() => collectTree(authList), []);
    const allOptions = useMemo(() => collectOptions(authList), []);
    const selected = new Set(value);

    // 以 Vue 权限树顺序提交，避免 React 交互改变后端权限数组的稳定性。
    const updateSelection = (next: Set<string>) => {
        onChange?.(allOptions.filter(option => next.has(option.auth)).map(option => option.auth));
    };
    const toggleCodes = (codes: string[], checked: boolean) => {
        const next = new Set(selected);
        codes.forEach(code => (checked ? next.add(code) : next.delete(code)));
        updateSelection(next);
    };
    const getNodeOptions = (node: RolePermissionNode): RolePermissionOption[] => [
        ...node.options,
        ...node.children.flatMap(child => getNodeOptions(child))
    ];
    const getNodeState = (node: RolePermissionNode) => {
        const options = getNodeOptions(node);
        const selectedCount = options.filter(option => selected.has(option.auth)).length;
        return {
            checked: options.length > 0 && selectedCount === options.length,
            indeterminate: selectedCount > 0 && selectedCount < options.length,
            options
        };
    };
    const toggleNode = (node: RolePermissionNode, checked: boolean) => {
        toggleCodes(
            getNodeOptions(node).map(option => option.auth),
            checked
        );
    };
    // Vue 权限行以“查看”为基础权限：选择其他动作自动带出查看，取消查看会清空同一行其他动作。
    const toggleOption = (option: RolePermissionOption, checked: boolean) => {
        const next = new Set(selected);
        const groupOptions = allOptions.filter(item => item.groupKey === option.groupKey);
        if (checked) {
            next.add(option.auth);
            if (option.permissionIndex > 0) {
                const readOption = groupOptions[0];
                if (readOption) next.add(readOption.auth);
            }
        } else {
            next.delete(option.auth);
            if (option.permissionIndex === 0) {
                groupOptions.forEach(item => next.delete(item.auth));
            }
        }
        updateSelection(next);
    };
    const selectedCount = allOptions.filter(option => selected.has(option.auth)).length;
    const allChecked = allOptions.length > 0 && selectedCount === allOptions.length;
    const allIndeterminate = selectedCount > 0 && !allChecked;

    return (
        <div aria-label="role-permission-selector" className="flex flex-col gap-3">
            <ZhCheckbox
                checked={allChecked}
                disabled={disabled}
                indeterminate={allIndeterminate}
                onChange={event =>
                    toggleCodes(
                        allOptions.map(option => option.auth),
                        event.target.checked
                    )
                }
            >
                全选
            </ZhCheckbox>
            {groups.map(group => {
                const renderNode = (node: RolePermissionNode, level: number): ReactNode => {
                    const state = getNodeState(node);
                    return (
                        <div
                            key={node.path.join("/")}
                            className={level === 0 ? "rounded border border-slate-200 p-3" : "mt-2 pl-6"}
                        >
                            <ZhCheckbox
                                aria-label={node.path.join(" / ")}
                                checked={state.checked}
                                disabled={disabled}
                                indeterminate={state.indeterminate}
                                onChange={event => toggleNode(node, event.target.checked)}
                            >
                                {node.label}
                            </ZhCheckbox>
                            {node.children.length > 0 ? (
                                <div>{node.children.map(child => renderNode(child, level + 1))}</div>
                            ) : (
                                <div className="mt-2 flex flex-wrap gap-x-6 gap-y-2 pl-6">
                                    {state.options.map(option => {
                                        const optionLabel = `${option.path.join(" / ")} - ${option.label}`;
                                        return (
                                            <ZhCheckbox
                                                key={`${option.groupKey}:${option.auth}`}
                                                aria-label={optionLabel}
                                                checked={selected.has(option.auth)}
                                                disabled={disabled}
                                                onChange={event => toggleOption(option, event.target.checked)}
                                            >
                                                {optionLabel}
                                            </ZhCheckbox>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                };

                return renderNode(group, 0);
            })}
        </div>
    );
}

export default RolePermissionSelector;
